import { useEffect, useState } from 'react';
import {
  MultipleVsIndustryTableWrapper,
  ShowMoreButton,
  ButtonWrapper,
  FillerMessage,
  ActionSheetTableWrapper,
  Hidden,
  StyledGrid,
} from './styled';
import TabSelection from './components/TabSelection';
import DataTable from './components/DataTable';
import { formatMultiple } from '../../../../utils';

import loadable from '@loadable/component';
import { useIndustryCompanies } from '../../hooks/useIndustryCompanies';
import { useIndustryContext } from '../IndustryContextProvider';
import ActionSheet, {
  Header,
  Panel,
  Sheet,
  Background,
} from '@/components/ActionSheet';
import {
  Grid,
  useTheme,
  Heading,
  Box,
  Text,
  usePreventWindowScroll,
} from '@simplywallst/ui-core';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';

const PreventWindowScroll = () => {
  usePreventWindowScroll();
  return null;
};

const ActionSheetTable = loadable(
  () => import('./components/ActionSheetTable')
);

const MultipleVsIndustryTable = () => {
  const { index, multiple, setIndex } = useIndustryContext();
  const defaultIndex = index === -1 ? 0 : index;
  const theme = useTheme();
  const limit = 4;
  //Get info about selected company from API
  const selected = useValuationData();
  const { multiples, tickerSymbol, uniqueSymbol, canonicalUrl } = selected;
  const defaultMultiple = multiple || multiples.preferredMultiple.name;
  const {
    companies,
    company,
    companyIndex,
    isCompanyInIndex,
    industryCompanies,
  } = useIndustryCompanies({
    tickerSymbol,
    index: defaultIndex,
    limit,
    selectedMultiple: defaultMultiple,
    uniqueSymbol,
    canonicalUrl,
    multiples,
  });

  useEffect(() => {
    if (companyIndex >= 0) setIndex(companyIndex);
  }, [companyIndex]);

  const totalCompaniesInBucket =
    industryCompanies.companies[defaultIndex]?.count || 0;
  const [open, setOpen] = useState(false);

  const industry = {
    name: selected.industrySecondaryName,
    ratioFormatted: formatMultiple(industryCompanies.median),
    salesGrowthFormatted: selected.revenueGrowthAnnualIndustryFormatted,
    earningsGrowthFormatted: selected.netIncomeGrowthAnnualIndustryFormatted,
  };

  const showButton =
    companies && totalCompaniesInBucket > (isCompanyInIndex ? 3 : limit);
  const loading = Boolean(limit && !companies);

  const companiesToShow = isCompanyInIndex ? 2 : limit;

  const t = useValuationTranslation();

  return (
    <MultipleVsIndustryTableWrapper>
      <TabSelection
        industryCompanies={industryCompanies}
        preferredMultiple={defaultMultiple}
      />
      <DataTable
        data-cy-id={'valuation-multiple-vs-industry-table'}
        company={company}
        companies={companies ? companies.slice(0, companiesToShow) : []}
        numberOfCompanies={totalCompaniesInBucket}
        companiesIsLoading={loading}
        companiesToShow={companiesToShow}
        preferredMultiple={defaultMultiple}
        industry={industry}
        isCompanyInCurrentRange={isCompanyInIndex}
      />
      {showButton ? (
        <ButtonWrapper>
          <ShowMoreButton
            styleType="loudLarge"
            display="block"
            onClick={() => setOpen(true)}
            data-cy-id="vs-industry-show-more"
          >
            {t('vsIndustry.table.showMore', {
              count: totalCompaniesInBucket,
            })}
          </ShowMoreButton>
        </ButtonWrapper>
      ) : (
        <FillerMessage
          companiesMissing={
            companies ? Math.max(companiesToShow - companies.length, 0) : 0
          }
        >
          {companies &&
            t('vsIndustry.table.noMore', {
              metric: defaultMultiple,
            })}
        </FillerMessage>
      )}

      <ActionSheet open={open} onClose={() => setOpen(false)}>
        <PreventWindowScroll />
        <Background onClick={() => setOpen(false)} />
        <Sheet>
          <Grid templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}>
            <Panel templateRows="max-content 1fr" row="2">
              <Header>
                <Box paddingBottom="x3">
                  <Heading
                    data-cy-id="vs-industry-action-sheet-table-heading"
                    as="h3"
                    typography="xLargeStrong"
                    color="white"
                  >
                    {defaultMultiple} Ratio vs. Industry
                  </Heading>

                  <Text
                    data-cy-id="vs-industry-action-sheet-table-subheading"
                    typography="small"
                    color="whiteRGBA"
                    opacity={0.5}
                  >
                    <>
                      {industryCompanies.count && industry.name ? (
                        t('vsIndustry.table.subHeading', {
                          count: industryCompanies.count,
                          industryName: industry.name,
                        })
                      ) : (
                        <>&nbsp;</>
                      )}
                    </>
                  </Text>
                </Box>
              </Header>
              <StyledGrid>
                <ActionSheetTableWrapper>
                  <ActionSheetTable
                    numberOfCompanies={totalCompaniesInBucket}
                    companiesIsLoading={loading}
                    industry={industry}
                    isCompanyInCurrentRange={isCompanyInIndex}
                  />
                </ActionSheetTableWrapper>
              </StyledGrid>
            </Panel>
          </Grid>
        </Sheet>
      </ActionSheet>
      {/* Keep markup in HTML still when closed */}
      {!open && (
        <Hidden>
          <ActionSheetTable
            numberOfCompanies={totalCompaniesInBucket}
            companiesIsLoading={loading}
            industry={industry}
            isCompanyInCurrentRange={isCompanyInIndex}
          />
        </Hidden>
      )}
    </MultipleVsIndustryTableWrapper>
  );
};
export default MultipleVsIndustryTable;
